import { CrossingBucket } from '@hakimo-ui/hakimo/types';
import { useMemo } from 'react';
import {
  BucketChartFrame,
  bucketSlot,
  bucketTime,
  GUTTER_LEFT,
  PLOT_HEIGHT,
  plotY,
} from '../bucket-chart/BucketChartFrame';

interface Props {
  crossings: CrossingBucket[];
}

/**
 * Crossings per bucket, each direction its own bar, with the inferred
 * appearances and vanishes as a third, hollow bar. Kept apart because a rule
 * only counts inferences when it opts in, and the chart is how someone
 * decides whether to.
 */
export function CrossingsChart({ crossings }: Props) {
  const slot = bucketSlot(crossings.length);
  const bar = Math.max(1, (slot - 4) / 3);

  const yMax = useMemo(
    () =>
      Math.max(1, ...crossings.map((b) => Math.max(b.ltr, b.rtl, b.inferred))),
    [crossings]
  );
  const barProps = (value: number, i: number) => ({
    x: GUTTER_LEFT + i * slot + 2,
    y: plotY(value, yMax),
    width: bar,
    height: PLOT_HEIGHT - plotY(value, yMax),
  });

  return (
    <BucketChartFrame
      ariaLabel="Crossings over time"
      bucketStarts={crossings.map((b) => b.bucketStartUtc)}
      yMax={yMax}
    >
      {crossings.map((b, i) => {
        const when = bucketTime(b.bucketStartUtc);
        const ltr = barProps(b.ltr, i);
        const rtl = barProps(b.rtl, i);
        const inferred = barProps(b.inferred, i);
        return (
          <g key={b.bucketStartUtc}>
            <rect {...ltr} className="fill-sky-600 dark:fill-sky-400">
              <title>{`${when} — ${b.ltr} in the arrow's direction`}</title>
            </rect>
            <rect
              {...rtl}
              x={rtl.x + bar}
              className="fill-amber-500 dark:fill-amber-400"
            >
              <title>{`${when} — ${b.rtl} against the arrow`}</title>
            </rect>
            <rect
              {...inferred}
              x={inferred.x + 2 * bar}
              fill="none"
              className="stroke-onlight-text-3 dark:stroke-ondark-text-3"
              strokeWidth={1}
              strokeDasharray="2 2"
            >
              <title>{`${when} — ${b.inferred} inferred (appeared or vanished at the line)`}</title>
            </rect>
          </g>
        );
      })}
    </BucketChartFrame>
  );
}

export default CrossingsChart;
